import { CircularProgress, debounce } from '@mui/material'
import {
	type ChangeEvent,
	type FC,
	type ReactElement,
	useEffect,
	useMemo,
	useState
} from 'react'

import { useAppSelector } from '../../../../../shared/hooks'
import TextFieldBig from '../../../../atoms/TextFieldBig'
import type { TExportContentProps, TExportFromUrlTypes } from '../../types.ts'
import ExportContent from '../ExportContent/ExportContent.tsx'
import ExportStep from '../ExportStep/ExportStep.tsx'

const ExportFromUrl: FC<TExportContentProps> = ({
	contactsCount,
	option,
	setContactsCount,
	setDataToExport
}: TExportContentProps): ReactElement => {
	const exportType = option.value as TExportFromUrlTypes
	const { appExportConnector } = useAppSelector(state => state.app)
	const profilesExport = appExportConnector?.profilesExport
	const [isChecking, setIsChecking] = useState(false)
	const [url, setUrl] = useState<string>('')
	const [error, setError] = useState<string>('')

	const onUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
		setUrl(event.target.value)
	}

	const { placeholder = '', title = '' } = option

	const showError = url.length > 0 && !!error && !isChecking

	const debouncedCheck = useMemo(
		() =>
			debounce(async (currentUrl: string) => {
				if (!currentUrl.length) {
					setError('')
					setContactsCount(null)
					setDataToExport(null)

					return
				}
				if (!profilesExport?.[exportType]) {
					setContactsCount(null)
					setDataToExport(null)
					return
				}

				try {
					setIsChecking(true)

					if (!profilesExport[exportType].isValid(currentUrl)) {
						setContactsCount(null)
						setDataToExport(null)
						setError('Check your link and try again')
						return
					}

					const count = await profilesExport[exportType].count(currentUrl)

					setError('')
					setContactsCount(typeof count === 'number' ? count : null)
					setDataToExport(currentUrl)
				} catch (e) {
					if (e instanceof Error) {
						if (e.name === 'SalesNavigatorNotAuthorizedError') {
							setError(
								"This import requires active Sales Navigator subscription, looks like you don't have it."
							)
						} else {
							setError(e.message)
						}
					} else {
						setError('Something went wrong, please try again')
					}
				} finally {
					setIsChecking(false)
				}
			}, 300),
		[profilesExport, setContactsCount, setDataToExport]
	)

	useEffect(() => {
		debouncedCheck(url)

		return () => {
			debouncedCheck.clear()
		}
	}, [url, debouncedCheck])

	return (
		<ExportStep title={title}>
			<ExportContent contactsCount={contactsCount} option={option}>
				<TextFieldBig
					autoComplete='new-password'
					endAdornment={isChecking ? <CircularProgress size='24px' /> : null}
					error={showError}
					helperText={showError ? error : ''}
					onChange={onUrlChange}
					placeholder={placeholder}
					sx={{
						'&.MuiTextField-root': {
							'& .MuiInputBase-root': {
								'&:not(.Mui-error):hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'var(--primary)'
								},
								'.MuiOutlinedInput-notchedOutline': {
									borderWidth: '1px'
								}
							},
							width: '100%'
						},
						'.MuiFormHelperText-root.Mui-error': {
							color: 'var(--red-indicator)',
							fontSize: '12px',
							fontWeight: 600,
							mx: 0,
							textAlign: 'left'
						}
					}}
					value={url}
				/>
			</ExportContent>
		</ExportStep>
	)
}

export default ExportFromUrl
